<template>
  <section id="batch-weather-table">
    <b-table
      :data="items"
      :mobile-cards="false"
      narrowed
      sticky-header
      :row-class="rowClass"
      :height="height"
    >
      <b-table-column label="" width="40" centered v-slot="props">
        <b-tooltip v-show="props.row.error" position="is-right">
          <b-icon icon="information" type="is-danger"></b-icon>
          <template v-slot:content>
            {{ props.row.error }}
          </template>
        </b-tooltip>
      </b-table-column>

      <b-table-column
        field="code"
        :label="$t('attributes.group_code')"
        width="100"
        centered
        sortable
        v-slot="props"
      >
        <b-tooltip type="is-light" position="is-right">
          <p>{{ props.row.code }}</p>
          <template v-slot:content>
            <p v-html="props.row.name"></p>
          </template>
        </b-tooltip>
      </b-table-column>

      <b-table-column
        field="departure_date"
        :label="$t('attributes.departure_date')"
        width="100"
        sortable
        centered
        v-slot="props"
      >
        {{ formatDate(props.row.departure_date) }}
      </b-table-column>

      <b-table-column
        field="cities"
        :label="$t('attributes.city')"
        v-slot="props"
      >
        <b-taginput
          :ref="`cities-input-${props.row.code}`"
          v-model="props.row.cityList"
          :data="filteredData"
          :maxtags="maxCities"
          :has-counter="false"
          autocomplete
          keep-first
          group-field="display_name"
          group-options="cities"
          icon="label"
          :placeholder="
            props.row.cities.length > maxCities
              ? `最多只可選${maxCities}個`
              : '選擇城市'
          "
          @typing="search => filteringData(props.row, search)"
          @input="modified(props.row)"
        >
          <template v-slot="options">
            {{ options.option.english_name }} {{ options.option.name }}
          </template>
          <template #selected="options">
            <b-tag
              v-for="(tag, index) in options.tags"
              :key="index"
              :tabstop="false"
              closable
              @close="
                $refs[`cities-input-${props.row.code}`].removeTag(index, $event)
              "
            >
              {{ tag.name }}
            </b-tag>
          </template>
          <template #empty>
            搜尋不到
          </template>
        </b-taginput>
      </b-table-column>

      <b-table-column
        width="50"
        :label="$t('attributes.function')"
        centered
        v-slot="props"
      >
        <div class="buttons is-centered">
          <b-tooltip :label="$t('attributes.restore')">
            <b-button
              type="is-info"
              icon-left="restore"
              outlined
              @click.prevent="restore(props.row)"
            >
            </b-button>
          </b-tooltip>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section" style="height: 100%">
          <div v-if="!loading" class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-loading :active.sync="isLoading" :is-full-page="false"></b-loading>
  </section>
</template>

<script>
import { WEATHER_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'

export default {
  name: 'BatchWeatherTable',
  props: {
    groups: {
      Type: Array,
      required: true
    },
    height: Number,
    loading: Boolean
  },
  data() {
    return {
      items: this.extractGroups(this.groups),

      loadingData: false,
      countries: [],
      cities: [],
      maxCities: 10,
      data: [],
      filteredData: []
    }
  },
  computed: {
    isLoading() {
      return this.loading || this.loadingData
    }
  },
  watch: {
    groups(list) {
      this.items = this.extractGroups(list)
    }
  },
  mounted() {
    this.loadDate()
  },
  methods: {
    async loadDate() {
      this.loadingData = true
      await this.loadCountries()
      await this.loadCities()
      this.loadingData = false
    },
    async loadCountries() {
      try {
        const api = `${WEATHER_API}/countries`
        const res = await this.axios.get(api)
        this.countries = res.data.data.map(c => ({ ...c, code: c.code.trim() }))
      } catch (e) {
        this.countries = []

        notifyService.fetch_error('weather_country_list', e)
      }
    },
    async loadCities() {
      const api = `${WEATHER_API}/list`

      try {
        const res = await this.axios.get(api)
        this.cities = res.data.data
          .filter(c => !!c.country_code)
          .map(city => {
            return {
              ...city,
              city_code: city.city_code.trim(),
              country_code: city.country_code.trim(),
              country: this.countries.find(
                country => country.code === city.country_code
              )
            }
          })

        this.items = this.items.map(group => {
          const cityList = group.cities.map(cityCode => {
            return this.cities.find(c => c.city_code === cityCode)
          })

          return {
            ...group,
            cityList: cityList
          }
        })

        this.data = this.countries.map(country => {
          return {
            ...country,
            display_name: `${country.english_name} ${country.name}`,
            cities: this.cities.filter(c => c.country_code === country.code)
          }
        })
      } catch (e) {
        this.cities = []

        notifyService.fetch_error('weather', e)
      }
    },
    filteringData(group, search) {
      const name = search.trim().toLowerCase()

      const newData = []
      this.data.forEach(element => {
        const found =
          element.name
            .trim()
            .toLowerCase()
            .includes(name) ||
          element.english_name
            .trim()
            .toLowerCase()
            .includes(name)

        if (found && element.cities.length > 0) {
          newData.push(element)
        } else {
          const cities = element.cities.filter(
            city =>
              city.name
                .trim()
                .toLowerCase()
                .includes(name) ||
              city.english_name
                .trim()
                .toLowerCase()
                .includes(name)
          )
          if (cities.length) {
            newData.push({ ...element, cities })
          }
        }
      })

      this.filteredData = newData
    },
    extractGroups(groups) {
      return groups.map(group => {
        const cities = group.city_weather ? group.city_weather.split(',') : []
        const cityList = this.cities
          ? cities.map(cityCode => {
              return this.cities.find(c => c.city_code === cityCode)
            })
          : []

        return {
          code: group.code,
          departure_date: group.departure_date,
          name: group.name,
          city_weather: group.city_weather,
          cities: cities,
          cityList: cityList,

          editing: false,
          dirty: false,
          error: null,
          origin: {
            ...group,
            cities: cities
          }
        }
      })
    },

    modified(group) {
      group.cities = group.cityList.map(c => c.city_code)
      group.dirty = group.cities !== group.origin.cities

      this.validate(group)
    },
    restore(group) {
      group.cities = [...group.origin.cities]
      group.cityList = group.cities.map(cityCode => {
        return this.cities.find(c => c.city_code === cityCode)
      })

      group.dirty = false
      group.error = false
    },

    isDirty() {
      return this.items.filter(x => x.dirty).length > 0
    },
    isEditing() {
      return this.items.filter(x => x.editing).length > 0
    },
    isValidate() {
      for (const group of this.items) {
        if (!this.validate(group)) {
          return false
        }
      }

      return true
    },
    validate(group) {
      group.error = null
      return true
    },
    getData() {
      return this.items
        .filter(group => group.dirty)
        .map(group => ({
          code: group.code,
          cities: group.cities.join(',')
        }))
    },
    rowClass(group) {
      if (group.error) {
        return 'has-background-danger-light'
      } else if (group.dirty) {
        return 'has-background-warning'
      } else {
        return null
      }
    }
  }
}
</script>

<style scoped></style>
